import { z } from "zod";

// ============================================
// COMPARISON OPERATORS (MongoDB-like syntax)
// ============================================

// Primitive values for comparisons
const primitiveValueSchema = z.union([z.string(), z.number(), z.boolean(), z.null()]);

// Relative date value schema (e.g., "30 days ago", "1 week ago")
const relativeDateValueSchema = z.object({
  $relative: z.object({
    amount: z.number().min(0),
    unit: z.enum(["days", "weeks", "months", "years"]),
    direction: z.enum(["ago"]), // Extensible for future "fromNow"
  }),
});

export type RelativeDateValue = z.infer<typeof relativeDateValueSchema>;

// Dynamic date values: built-in ($now, $today) or relative ($relative)
const dynamicDateValueSchema = z.union([z.enum(["$now", "$today"]), relativeDateValueSchema]);

export type DynamicDateValue = z.infer<typeof dynamicDateValueSchema>;

// Comparison operator schemas
export const comparisonOperatorSchema = z.union([
  // Equality
  z.object({ $eq: primitiveValueSchema }),
  z.object({ $ne: primitiveValueSchema }),

  // Numeric/Date comparisons (support $now/$today and $relative for dates)
  z.object({ $gt: z.union([z.number(), dynamicDateValueSchema]) }),
  z.object({ $gte: z.union([z.number(), dynamicDateValueSchema]) }),
  z.object({ $lt: z.union([z.number(), dynamicDateValueSchema]) }),
  z.object({ $lte: z.union([z.number(), dynamicDateValueSchema]) }),

  // Array/set operations
  z.object({ $in: z.array(primitiveValueSchema) }),
  z.object({ $nin: z.array(primitiveValueSchema) }),

  // Existence check
  z.object({ $exists: z.boolean() }),

  // Regex matching
  z.object({ $regex: z.string() }),
]);

export type ComparisonOperator = z.infer<typeof comparisonOperatorSchema>;

// ============================================
// DATA SOURCES (extensible for future phases)
// ============================================

// Data source types for visibility conditions
// Phase 1: Only "enduser" is fully implemented
// Future sources are included in the schema for forward compatibility
export const dataSourceTypeSchema = z.enum([
  "enduser", // Current patient's profile fields (Phase 1)
  "formResponse", // Form submission data
  "stripeSubscription", // Stripe subscription data
  // Item filter sources (for filtering items within data blocks)
  "form", // Form definition (for pinned forms filtering)
  "appointment", // Appointment/CalendarEvent data
  "appointmentBookingPage", // AppointmentBookingPage data
  "order", // EnduserOrder data
  "chatRoom", // ChatRoom data
  "file", // File data
  "content", // ManagedContentRecord data
  "purchase", // Purchase data
  "stripeInvoice", // Stripe invoice data
]);

export type DataSourceType = z.infer<typeof dataSourceTypeSchema>;

/**
 * Value stored in an enduser `customTypeId` (Contact Type) condition to target contacts with no custom type,
 * i.e. `customTypeId` unset or `""`. Mirrors the Tellescope 1 convention for stored entity-type lists.
 */
export const DEFAULT_CONTACT_TYPE_ID = "Default";

// ============================================
// LEAF CONDITION (field check with data source)
// ============================================

// Leaf condition: check a specific field from a data source
// Identified by presence of "source" + "field" (no $and/$or/$not)
export const leafConditionSchema = z.object({
  source: dataSourceTypeSchema, // Data source type (e.g., "enduser", future: "stripe")
  field: z.string(), // Dot-notation path, e.g., "state", "insurance.payerName"
  value: z.union([primitiveValueSchema, comparisonOperatorSchema]),
});

export type LeafCondition = z.infer<typeof leafConditionSchema>;

// ============================================
// ELEM MATCH CONDITION (per-item grouped evaluation)
// ============================================

// Field-only condition used inside $elemMatch groups (no source — source is on the parent group)
export const itemConditionSchema = z.object({
  field: z.string(),
  value: z.union([primitiveValueSchema, comparisonOperatorSchema]),
});

export type ItemCondition = z.infer<typeof itemConditionSchema>;

// $elemMatch: returns true if at least one item in the source array satisfies ALL conditions simultaneously
export const elemMatchConditionSchema = z.object({
  $elemMatch: z.object({
    source: dataSourceTypeSchema,
    conditions: z.array(itemConditionSchema).min(1),
  }),
});

export type ElemMatchCondition = z.infer<typeof elemMatchConditionSchema>;

// ============================================
// RECURSIVE VISIBILITY CONDITION ($and/$or/$not/$elemMatch)
// ============================================

// The condition tree supports full recursive nesting
// Type detection:
//   - Has "$and" → logical AND
//   - Has "$or" → logical OR
//   - Has "$not" → logical NOT
//   - Has "$elemMatch" → per-item grouped evaluation
//   - Otherwise (has "source" + "field") → leaf condition
export type VisibilityCondition =
  | LeafCondition // Leaf: source + field + value
  | { $and: VisibilityCondition[] } // All must be true
  | { $or: VisibilityCondition[] } // At least one must be true
  | { $not: VisibilityCondition } // Negate the result
  | ElemMatchCondition; // Per-item: at least one item satisfies all conditions

export const visibilityConditionSchema: z.ZodType<VisibilityCondition> = z.lazy(() =>
  z.union([
    leafConditionSchema,
    z.object({ $and: z.array(visibilityConditionSchema) }),
    z.object({ $or: z.array(visibilityConditionSchema) }),
    z.object({ $not: visibilityConditionSchema }),
    elemMatchConditionSchema,
  ])
);

// ============================================
// BLOCK VISIBILITY CONFIGURATION
// ============================================

export const blockVisibilitySchema = z.object({
  condition: visibilityConditionSchema,
  description: z.string().optional(), // Admin UI description
});

export type BlockVisibility = z.infer<typeof blockVisibilitySchema>;

// ============================================
// TYPE GUARDS
// ============================================

export function isLeafCondition(condition: VisibilityCondition): condition is LeafCondition {
  return "source" in condition && "field" in condition;
}

export function isAndCondition(condition: VisibilityCondition): condition is { $and: VisibilityCondition[] } {
  return "$and" in condition;
}

export function isOrCondition(condition: VisibilityCondition): condition is { $or: VisibilityCondition[] } {
  return "$or" in condition;
}

export function isNotCondition(condition: VisibilityCondition): condition is { $not: VisibilityCondition } {
  return "$not" in condition;
}

export function isElemMatchCondition(condition: VisibilityCondition): condition is ElemMatchCondition {
  return "$elemMatch" in condition;
}

// Keys that identify a ComparisonOperator object
const COMPARISON_OPERATOR_KEYS = [
  "$eq",
  "$ne",
  "$gt",
  "$gte",
  "$lt",
  "$lte",
  "$in",
  "$nin",
  "$exists",
  "$regex",
] as const;

export function isComparisonOperator(value: unknown): value is ComparisonOperator {
  if (typeof value !== "object" || value === null) return false;
  return COMPARISON_OPERATOR_KEYS.some((key) => key in value);
}
